"use client";

import { motion } from "motion/react";
import { usePathname, useRouter } from "../lib/router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type TransitionDirection = -1 | 1;
type TransitionStage = "cover" | "reveal";

type TransitionState = {
  href: string;
  direction: TransitionDirection;
  stage: TransitionStage;
};

const PageTransitionContext = createContext<{
  navigate: (href: string, direction: TransitionDirection) => void;
} | null>(null);

const curtainVariants = {
  initial: (direction: TransitionDirection) => ({ x: `${direction * 100}vw` }),
  cover: { x: 0 },
  reveal: (direction: TransitionDirection) => ({ x: `${direction * -100}vw` }),
};

/** 为导航栏提供方向明确的全屏幕帘跳转。 */
export function usePageTransition() {
  const context = useContext(PageTransitionContext);

  if (!context)
    throw new Error("usePageTransition 必须在 PageTransition 内使用。");

  return context;
}

/** 用全屏幕帘遮住页面替换过程，避免两页内容同时出现。 */
export function PageTransition({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [transition, setTransition] = useState<TransitionState | null>(null);

  const navigate = useCallback(
    (href: string, direction: TransitionDirection) => {
      if (transition || href === pathname) return;
      setTransition({ href, direction, stage: "cover" });
    },
    [pathname, transition],
  );

  useEffect(() => {
    if (!transition || transition.stage !== "cover") return;
    if (pathname !== transition.href) return;

    const frame = requestAnimationFrame(() => {
      setTransition((current) =>
        current ? { ...current, stage: "reveal" } : null,
      );
    });

    return () => cancelAnimationFrame(frame);
  }, [pathname, transition]);

  /** 覆盖完成后再替换路由，揭开完成后移除过渡帘。 */
  const handleAnimationComplete = () => {
    if (!transition) return;

    if (transition.stage === "cover") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      router.push(transition.href, { scroll: true });
      return;
    }

    setTransition(null);
  };

  return (
    <PageTransitionContext.Provider value={{ navigate }}>
      {children}
      {transition && (
        <motion.div
          aria-hidden="true"
          className="page-transition-curtain"
          custom={transition.direction}
          variants={curtainVariants}
          initial="initial"
          animate={transition.stage}
          transition={{
            duration: transition.stage === "cover" ? 0.14 : 0.22,
            ease: [0.65, 0, 0.35, 1],
          }}
          onAnimationComplete={handleAnimationComplete}
        />
      )}
    </PageTransitionContext.Provider>
  );
}
